import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { postJson } from "@/hooks/use-shares";
import { getJson } from "@/lib/api-client";
import type { PhotoFlag } from "@/lib/enums";
import { aiReviewKeys, checklistKeys, projectKeys } from "@/lib/query";
import {
  type AiReviewJob,
  aiReviewJobListResponseSchema,
  aiReviewJobResponseSchema,
  albumReviewStatusResponseSchema,
  photoReviewResponseSchema,
} from "@/schemas/ai-review";

/** How often the active jobs are asked for while one is queued or running. */
export const AI_JOB_POLL_MS = 3000;

export function isActiveJob(job: AiReviewJob): boolean {
  return job.status === "queued" || job.status === "running";
}

/** Poll only while a job is queued or running; otherwise not at all. */
export function aiJobsPollInterval(jobs: AiReviewJob[] | undefined): number | false {
  return jobs?.some(isActiveJob) ? AI_JOB_POLL_MS : false;
}

/** Ids that were active in `before` and are not in `after`: those jobs have finished. */
export function finishedJobIds(before: AiReviewJob[], after: AiReviewJob[]): string[] {
  const still = new Set(after.map((job) => job.id));
  return before.filter((job) => !still.has(job.id)).map((job) => job.id);
}

/** What a PM is told when a check ends: counts when it read something, the reason when not. */
export function jobFinishedMessage(job: AiReviewJob): { ok: boolean; text: string } {
  if (job.status === "failed") return { ok: false, text: job.error ?? "The AI check failed." };
  const read = `${job.reviewed.toLocaleString("en-US")} ${job.reviewed === 1 ? "photo" : "photos"} read`;
  const failed = job.failed > 0 ? `, ${job.failed.toLocaleString("en-US")} could not be read` : "";
  return { ok: true, text: `AI check done: ${read}${failed}` };
}

/** What the jobs query holds: the queued and running jobs, and any that just finished. */
export interface AiJobsState {
  active: AiReviewJob[];
  finished: AiReviewJob[];
}

const jobsUrl = (projectId: string, active: boolean) =>
  `/api/projects/${encodeURIComponent(projectId)}/ai-review-jobs${active ? "?active=1" : ""}`;

/**
 * The project's queued and running AI checks (PM and admin only; never call it for an IE). A job
 * that drops out of the active list is looked up once in the recent list, so its final counts
 * can be reported.
 */
export function useActiveAiJobs(projectId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: aiReviewKeys.jobs(projectId),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<AiJobsState> => {
      const before = queryClient.getQueryData<AiJobsState>(aiReviewKeys.jobs(projectId));
      const active = (await getJson(jobsUrl(projectId, true), aiReviewJobListResponseSchema)).data;
      const gone = finishedJobIds(before?.active ?? [], active);
      if (gone.length === 0) return { active, finished: [] };
      const recent = (await getJson(jobsUrl(projectId, false), aiReviewJobListResponseSchema)).data;
      return { active, finished: recent.filter((job) => gone.includes(job.id)) };
    },
    refetchInterval: (query) => aiJobsPollInterval(query.state.data?.active),
  });
}

function refreshAfterJob(queryClient: QueryClient, job: AiReviewJob) {
  void queryClient.invalidateQueries({ queryKey: aiReviewKeys.all });
  void queryClient.invalidateQueries({ queryKey: checklistKeys.all });
  // A single-photo check finishes while the PM is looking at that photo in the viewer, and the
  // list is what the viewer stands on: refetching it could close the viewer (a search or "Needs
  // a retake" filter may stop matching). It is marked stale instead.
  void queryClient.invalidateQueries({
    queryKey: projectKeys.all,
    ...(job.scope === "photo" ? { refetchType: "none" as const } : {}),
  });
}

/**
 * Mounted once per project page for a PM: tells the PM when a check ends and refreshes what it
 * changed. Toasts once per job, however many components read the jobs.
 */
export function useAiJobNotifier(projectId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const { data } = useActiveAiJobs(projectId, enabled);
  const told = useRef(new Set<string>());
  const finished = data?.finished;
  useEffect(() => {
    for (const job of finished ?? []) {
      if (told.current.has(job.id)) continue;
      told.current.add(job.id);
      const message = jobFinishedMessage(job);
      if (message.ok) toast.success(message.text);
      else toast.error(message.text);
      refreshAfterJob(queryClient, job);
    }
    // Told once: a page opened again later must not repeat it.
    if (finished && finished.length > 0) {
      queryClient.setQueryData<AiJobsState>(aiReviewKeys.jobs(projectId), (old) =>
        old ? { ...old, finished: [] } : old,
      );
    }
  }, [finished, queryClient, projectId]);
}

export function usePhotoReview(photoId: string) {
  return useQuery({
    queryKey: aiReviewKeys.photo(photoId),
    queryFn: async () =>
      (
        await getJson(
          `/api/photos/${encodeURIComponent(photoId)}/ai-review`,
          photoReviewResponseSchema,
        )
      ).data,
  });
}

/**
 * What an album's "Check with AI" needs before it sends anything: how many photos the AI can
 * read, how many it has read, and whether the album has a summary. PM and admin only. Refreshed
 * with the rest of the AI state when a check ends.
 */
export function useAlbumReviewStatus(albumId: string) {
  return useQuery({
    queryKey: aiReviewKeys.albumStatus(albumId),
    queryFn: async () =>
      (
        await getJson(
          `/api/albums/${encodeURIComponent(albumId)}/ai-review-status`,
          albumReviewStatusResponseSchema,
        )
      ).data,
  });
}

function useRequestReview(url: (id: string) => string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      aiReviewJobResponseSchema.parse(await postJson(url(id), {})).data,
    onSuccess: (job) => {
      // The server's own job goes into the list now, so a check that ends before the next poll
      // is still reported.
      queryClient.setQueryData<AiJobsState>(aiReviewKeys.jobs(projectId), (old) => ({
        active: [...(old?.active ?? []).filter((j) => j.id !== job.id), job],
        finished: [],
      }));
      void queryClient.invalidateQueries({ queryKey: aiReviewKeys.jobs(projectId) });
      toast.success(
        job.scope === "album" ? "Checking this album with AI" : "Checking this photo with AI",
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not start the AI check");
    },
  });
}

export function useRequestAlbumReview(projectId: string) {
  return useRequestReview(
    (albumId) => `/api/albums/${encodeURIComponent(albumId)}/ai-review`,
    projectId,
  );
}

export function useRequestPhotoReview(projectId: string) {
  return useRequestReview(
    (photoId) => `/api/photos/${encodeURIComponent(photoId)}/ai-review`,
    projectId,
  );
}

export function useDismissFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, flag }: { photoId: string; flag: PhotoFlag }) =>
      postJson(
        `/api/photos/${encodeURIComponent(photoId)}/flags/${encodeURIComponent(flag)}/dismiss`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiReviewKeys.all });
      // Under "Needs a retake" the photo may stop matching, and refetching the list now would
      // close the viewer on the PM mid-review. It is marked stale instead.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all, refetchType: "none" });
      toast.success("Flag dismissed");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not dismiss this flag");
    },
  });
}

function useDecideSuggestion(path: "confirm" | "reject", success: string, failure: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) =>
      postJson(`/api/checklist-suggestions/${encodeURIComponent(suggestionId)}/${path}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checklistKeys.all });
      // The header and the home grid show the suggested count.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success(success);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : failure);
    },
  });
}

export function useConfirmSuggestion() {
  return useDecideSuggestion(
    "confirm",
    "Photo linked to the item",
    "Could not confirm this suggestion",
  );
}

export function useRejectSuggestion() {
  return useDecideSuggestion("reject", "Suggestion rejected", "Could not reject this suggestion");
}
